import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertTriangle, Scale, Shield, BookOpen } from 'lucide-react';

const DISCLAIMER_ACCEPTED_KEY = 'ofs_disclaimer_accepted';
const DISCLAIMER_VERSION = '1.0'; // Increment to force re-acknowledgment

// Search-engine / link-preview crawlers cannot click "Accept"; the page content is identical for them,
// so the acknowledgement gate is simply not raised. Real browsers (incl. headless test runners) still see it.
const CRAWLER_UA = /googlebot|google-inspectiontool|adsbot-google|mediapartners-google|bingbot|bingpreview|slurp|duckduckbot|baiduspider|yandex(bot|images)|applebot|facebookexternalhit|twitterbot|linkedinbot|pinterestbot|slackbot|discordbot|telegrambot|whatsapp|embedly|ia_archiver|semrushbot|ahrefsbot|petalbot|lighthouse|chrome-lighthouse/i;
const isCrawler = () => typeof navigator !== 'undefined' && CRAWLER_UA.test(navigator.userAgent || '');

// Check if disclaimer was accepted before initial render
const getInitialOpenState = () => {
  if (typeof window === 'undefined') return false;
  if (isCrawler()) return false;
  const accepted = localStorage.getItem(DISCLAIMER_ACCEPTED_KEY);
  return accepted !== DISCLAIMER_VERSION;
};

export const DisclaimerModal = () => {
  const [isOpen, setIsOpen] = useState(getInitialOpenState);
  const [hasReadTerms, setHasReadTerms] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const handleAccept = () => {
    localStorage.setItem(DISCLAIMER_ACCEPTED_KEY, DISCLAIMER_VERSION);
    setIsOpen(false);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setHasScrolledToBottom(true);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="grid-rows-[auto_1fr_auto] gap-3 sm:gap-4 w-[calc(100%-1.5rem)] max-w-lg sm:max-w-2xl max-h-[92vh] overflow-hidden p-4 sm:p-6 bg-gradient-to-b from-slate-900 to-slate-800 border-slate-700"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="disclaimer-modal"
      >
        <DialogHeader className="space-y-1.5 sm:space-y-2 pb-2 sm:pb-3 border-b border-slate-700">
          <div className="flex items-center justify-center">
            <div className="p-2 sm:p-3 rounded-full bg-amber-500/20">
              <AlertTriangle className="h-6 w-6 sm:h-8 sm:w-8 text-amber-400" />
            </div>
          </div>
          <DialogTitle className="text-lg sm:text-2xl font-bold text-center text-white">
            Important Legal Disclaimer
          </DialogTitle>
          <DialogDescription className="text-center text-xs sm:text-sm text-slate-400">
            Please read and acknowledge the following terms before using Option Wizard
          </DialogDescription>
        </DialogHeader>

        <ScrollArea 
          className="h-full min-h-0 pr-3 sm:pr-4" 
          onScrollCapture={handleScroll}
        >
          <div className="space-y-4 sm:space-y-5 py-3 text-slate-300">
            {/* Section 1: Not Financial Advice */}
            <section className="space-y-2">
              <div className="flex items-center gap-2 text-amber-400">
                <Scale className="h-4 w-4 sm:h-5 sm:w-5" />
                <h3 className="font-semibold text-base sm:text-lg">Not Financial Advice</h3>
              </div>
              <p className="text-sm leading-relaxed">
                Option Wizard (&ldquo;the Platform&rdquo;) provides <strong className="text-white">educational tools and information only</strong>. 
                Nothing contained in this platform constitutes financial advice, investment advice, trading advice, 
                or any other sort of advice. The content provided is for informational and educational purposes only 
                and should not be construed as professional financial advice.
              </p>
            </section>

            {/* Section 2: Educational Purpose */}
            <section className="space-y-2">
              <div className="flex items-center gap-2 text-blue-400">
                <BookOpen className="h-4 w-4 sm:h-5 sm:w-5" />
                <h3 className="font-semibold text-base sm:text-lg">Educational Purpose Only</h3>
              </div>
              <p className="text-sm leading-relaxed">
                This platform is designed solely for <strong className="text-white">educational and informational purposes</strong>. 
                All features, including AI-generated analysis, strategy suggestions, portfolio analysis, 
                and market data visualizations, are provided as learning tools to help users understand 
                options trading concepts. They are not recommendations to buy, sell, or hold any securities.
              </p>
            </section>

            {/* Section 3: Use at Your Own Risk */}
            <section className="space-y-2">
              <div className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5" />
                <h3 className="font-semibold text-base sm:text-lg">Use at Your Own Risk</h3>
              </div>
              <p className="text-sm leading-relaxed">
                <strong className="text-white">You assume all risk</strong> associated with using this platform and any 
                decisions made based on information provided herein. Options trading involves substantial risk 
                of loss and is not suitable for all investors. Past performance is not indicative of future results. 
                You may lose more than your initial investment.
              </p>
            </section>

            {/* Section 4: No Guarantees */}
            <section className="space-y-2">
              <div className="flex items-center gap-2 text-purple-400">
                <Shield className="h-4 w-4 sm:h-5 sm:w-5" />
                <h3 className="font-semibold text-base sm:text-lg">No Guarantees or Warranties</h3>
              </div>
              <p className="text-sm leading-relaxed">
                The Platform, its creators, affiliates, and partners make <strong className="text-white">no representations 
                or warranties</strong> of any kind, express or implied, about the completeness, accuracy, reliability, 
                suitability, or availability of the platform or the information, products, services, or related 
                graphics contained on the platform. Any reliance you place on such information is strictly at your own risk.
              </p>
            </section>

            {/* Section 5: Data Accuracy */}
            <section className="space-y-2">
              <h3 className="font-semibold text-base sm:text-lg text-slate-200">Data Accuracy Disclaimer</h3>
              <p className="text-sm leading-relaxed">
                Market data, options pricing, and other financial information displayed on this platform may be 
                delayed, inaccurate, or incomplete. <strong className="text-white">Third-party data sources</strong> are used, 
                and we do not guarantee their accuracy. Always verify information with your broker or official 
                sources before making any trading decisions.
              </p>
            </section>

            {/* Section 6: AI Limitations */}
            <section className="space-y-2">
              <h3 className="font-semibold text-base sm:text-lg text-slate-200">AI-Generated Content Limitations</h3>
              <p className="text-sm leading-relaxed">
                This platform utilizes artificial intelligence to generate analysis and suggestions. 
                AI-generated content <strong className="text-white">may contain errors, inaccuracies, or inappropriate suggestions</strong>. 
                AI outputs should be considered as one of many inputs in your educational journey and 
                should never be the sole basis for any financial decision.
              </p>
            </section>

            {/* Section 7: Limitation of Liability */}
            <section className="space-y-2">
              <h3 className="font-semibold text-base sm:text-lg text-slate-200">Limitation of Liability</h3>
              <p className="text-sm leading-relaxed">
                To the fullest extent permitted by applicable law, <strong className="text-white">Option Wizard, 
                its owners, developers, affiliates, and partners shall not be liable</strong> for any direct, indirect, 
                incidental, special, consequential, or punitive damages, including but not limited to, loss of 
                profits, data, use, goodwill, or other intangible losses, resulting from:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-4">
                <li>Your access to or use of (or inability to access or use) the platform</li>
                <li>Any conduct or content of any third party on the platform</li>
                <li>Any content obtained from the platform</li>
                <li>Unauthorized access, use, or alteration of your transmissions or content</li>
                <li>Any trading losses incurred</li>
                <li>Any errors or inaccuracies in the data or analysis provided</li>
              </ul>
            </section>

            {/* Section 8: User Responsibility */}
            <section className="space-y-2">
              <h3 className="font-semibold text-base sm:text-lg text-slate-200">User Responsibility</h3>
              <p className="text-sm leading-relaxed">
                You are solely responsible for your own investment research and decisions. 
                <strong className="text-white"> Consult with a qualified, licensed financial advisor</strong> before making 
                any investment decisions. You acknowledge that you have read, understood, and agree to be 
                bound by these terms.
              </p>
            </section>

            {/* Section 9: Jurisdiction */}
            <section className="space-y-2">
              <h3 className="font-semibold text-base sm:text-lg text-slate-200">Governing Law</h3>
              <p className="text-sm leading-relaxed">
                These terms shall be governed by and construed in accordance with applicable laws, 
                without regard to conflict of law principles. Any disputes arising from the use of 
                this platform shall be resolved through binding arbitration.
              </p>
            </section>

            {/* Final Notice */}
            <section className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-3">
              <p className="text-sm font-medium text-red-300 text-center">
                ⚠️ OPTIONS TRADING INVOLVES SIGNIFICANT RISK OF LOSS. YOU CAN LOSE MORE THAN YOUR INITIAL INVESTMENT. 
                ONLY TRADE WITH MONEY YOU CAN AFFORD TO LOSE.
              </p>
            </section>
          </div>
        </ScrollArea>

        <DialogFooter className="flex flex-col sm:flex-col sm:space-x-0 space-y-3 pt-2 sm:pt-3 border-t border-slate-700">
          <div className="flex items-start space-x-3 w-full">
            <Checkbox 
              id="accept-terms"
              checked={hasReadTerms}
              onCheckedChange={(checked) => setHasReadTerms(checked)}
              className="mt-1 border-slate-500 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
              data-testid="disclaimer-checkbox"
            />
            <label 
              htmlFor="accept-terms" 
              className="text-sm text-slate-300 cursor-pointer leading-relaxed"
            >
              I have read and understood the above disclaimer. I acknowledge that this platform is for 
              <strong className="text-white"> educational purposes only</strong>, does not provide financial advice, 
              and I <strong className="text-white">use it at my own risk</strong>. I accept full responsibility 
              for any decisions I make based on information from this platform.
            </label>
          </div>
          
          <Button 
            onClick={handleAccept}
            disabled={!hasReadTerms}
            className="w-full h-11 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="disclaimer-accept-button"
          >
            I Understand and Accept
          </Button>
          
          <p className="text-xs text-slate-500 text-center">
            By clicking &ldquo;I Understand and Accept&rdquo;, you agree to be bound by these terms.
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DisclaimerModal;
